import React, { useState, useEffect } from "react";
import ActionItemsModal from "./ActionItemsModal";
import ActionItemsTable from "./ActionItemsTable";
import VirtualizedAutoComplete from "../../../../../../components/Dropdown/VirtualizedAutocomplete";
import API_BASE_URL from "../../../../../../Constants";

const CallLogForm = ({ isOpen, onClose, fetchCallLogs, mechanics, editingCallLog, isFollowUp = false }) => {
  const isEditing = Boolean(editingCallLog) && !isFollowUp;
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionItems, setActionItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    building: {},
    caller: {},
    assignedTo: null,
    logDateTime: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    status: "Active",
    priority: 1,
    taskNotes: "",
    callNotes: "",
    devices: [],
    actionItems: [],
    type: "T"
  });

  const [buildings, setBuildings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [buildingDevices, setBuildingDevices] = useState([]);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [viewActions, setViewActions] = useState(true);

  const mapType = {
    "T": "Trouble Call",
    "F": "Follow Up",
    "S": "Service"
  }



  useEffect(() => {
    if (!isEditing && !isFollowUp) {
      fetchBuildings();
      return;
    }

    if (isFollowUp) {
      setFormData({
        ...formData,
        callNumber: undefined,
        building: editingCallLog.building,
        caller: editingCallLog.caller,
        assignedTo: editingCallLog.assignedTo,
        status: "Active",
        type: "F",
        priority: editingCallLog.priority,
        taskNotes: "Follow Up on Call Number: " + editingCallLog.callNumber + "\n" + editingCallLog.taskNotes,
        callNotes: "Follow Up on Call Number: " + editingCallLog.callNumber + "\n" + editingCallLog.callNotes,
      });
    } else if (isEditing) {
      setFormData({ ...editingCallLog });
    }
    fetchActionItems(editingCallLog.callNumber);
  }, [editingCallLog]);



  useEffect(() => {

    if (formData.building.buildingID === undefined) return;

    setContacts([]);
    setBuildingDevices([]);

    if (!isEditing) {
      if (!isFollowUp) {
        setFormData((prev) => ({ ...prev, caller: {} }));
        setFormData((prev) => ({ ...prev, devices: [] }));
      }
      if (formData.building.buildingID) {
        fetchContacts(formData.building.buildingID);
      }
    }
    fetchBuildingDevices(formData.building.buildingID);

    if (isEditing || isFollowUp) {
      fetchCallLogDevices(editingCallLog.callNumber);
    }
  }, [formData.building]);


  const fetchActionItems = async (callNumber) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/callcenter/actionitems/call/${callNumber}`);
      const data = await response.json();

      let itemsTemp = [];
      if (isFollowUp) {
        for (let i = 0; i < data.length; i++) {

          if (!data[i].isCompleted) {
            delete data[i].callLog;
            data[i].scheduleDate = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
            data[i].assignedTo = editingCallLog.assignedTo;
            itemsTemp.push(data[i]);
          }
        }
      } else {
        itemsTemp = [...data];
      }
      setActionItems(itemsTemp);


    } catch (error) {
      console.error("Error fetching action items:", error);
    }
  };


  const fetchBuildings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/buildings/all`);
      const data = await response.json();
      setBuildings(data);
    } catch (error) {
      console.error("Error fetching buildings:", error);
    }
  };


  const fetchCallLogDevices = async (callNumber) => {

    if (!callNumber) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/callcenter/calllogs/${callNumber}/devices`);
      const data = await response.json();

      let deviceData = []

      for (let i = 0; i < data.length; i++) {
        deviceData.push(data[i].device);
      }

      setFormData((prev) => ({ ...prev, devices: deviceData || [] }));
    } catch (error) {
      console.error("Error fetching call log devices:", error);
    }
  };


  const fetchContacts = async (buildingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/building-contact-intersections/building/${buildingId}`);
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchBuildingDevices = async (buildingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`);
      const data = await response.json();
      setBuildingDevices(data);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  const handleDeviceSelection = (device) => {
    setFormData((prev) => {
      const alreadySelected = prev.devices.some((d) => d.deviceId === device.deviceId);
      if (alreadySelected) {
        return { ...prev, devices: prev.devices.filter((d) => d.deviceId !== device.deviceId) };
      } else {
        return { ...prev, devices: [...prev.devices, device] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;


    if (actionItems.length === 0) {
      alert("Please add at least one action item!");
      return;
    }

    if (formData.devices?.length === 0) {
      alert("Please select at least one device!");
      return;
    }

    if (!formData.caller.contactId && !formData.caller.contactID) {
      alert("Please select a caller contact!");
      return;
    }

    if (!formData.building.buildingID) {
      alert("Please select a building!");
      return;
    }


    setIsLoading(true);

    let updatedFormData = { ...formData, actionItems: actionItems };

    updatedFormData.caller = {
      contactID: formData.caller.contactID || formData.caller.contactId,
      contactName: formData.caller.contactName,
      phone1: formData.caller.phone1,
      emailAddress: formData.caller.emailAddress,
    };
    try {
      if (isEditing) {
        await fetch(`${API_BASE_URL}/api/callcenter/calllogs/${editingCallLog.callNumber}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updatedFormData),
        });
      } else {
        await fetch(`${API_BASE_URL}/api/callcenter/calllogs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updatedFormData),
        });
      }
      fetchCallLogs();
      let success_msg = isEditing ? "Call log updated successfully!" : isFollowUp ? "Follow up created successfully!" : "Call log created successfully!";
      alert(success_msg);
      onClose();
    } catch (error) {
      console.error("Error saving call log:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-30 mt-20 flex justify-center items-center ${isOpen ? "z-50" : "hidden"}`}>
      <div className="bg-white p-6 rounded-md shadow-lg w-2/3 mt-10 mb-10 h-3/4">
        <h2 className="text-xl font-bold mb-4">{isEditing ? "Edit Call #" + editingCallLog?.callNumber || "Unknown" : isFollowUp ? "Create Follow Up" : "Add New Call Log"}</h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3 overflow-y-scroll h-3/4 border-b">
          {/* Building Selection */}
          <>
            <label className="font-semibold">Building</label>

            {isEditing ? (
              <>
                <p className="border p-2 rounded">{formData.building.buildingReference}</p>
              </>
            ) : (
              <>
                <VirtualizedAutoComplete
                  options={buildings}
                  value={formData?.building}
                  onChange={(e, newValue) => setFormData({ ...formData, building: newValue || {} })}
                  label="Select Building Site"
                  optionKey="buildingID"
                  optionLabel="buildingReference"
                  disabled={isEditing || isFollowUp}
                />
              </>
            )}

            <label className="font-semibold">Caller Contact</label>

            {isEditing ? (
              <>
                <p className="border p-2 rounded">{formData.caller.contactName} - {formData.caller.cellPhone || formData.caller.phone1} - {formData.caller.emailAddress}

                </p>
              </>
            ) : (
              <>
                <VirtualizedAutoComplete
                  options={contacts}
                  value={formData.caller}
                  onChange={(e, newValue) => setFormData({ ...formData, caller: newValue || {} })}
                  label="Select Caller Contact"
                  optionKey="contactId"
                  optionLabel="contactName"
                  disabled={isEditing || isFollowUp}
                />
              </>
            )}

            {/* <label className="font-semibold">Assigned To (Mechanic)</label>
            <VirtualizedAutoComplete
              options={mechanics}
              value={formData?.assignedTo || {}}
              onChange={(e, newValue) => setFormData({ ...formData, assignedTo: newValue })}
              label="Assign Mechanic"
              optionKey="mechanicId"
              optionLabel="email"
            /> */}

            {/* Device Selection */}
            <label className="font-semibold">Devices</label>

            <button
              type="button"
              onClick={() => {
                if (isEditing) return;
                setIsDeviceModalOpen(true);
              }}
              className="bg-gray-200 text-black px-4 py-2 rounded m-2"
            >
              Select Devices
            </button>

            {/* Selected Devices */}
            <div className="mt-2">
              {formData.devices?.length > 0 ? (
                formData.devices.map((device) => (
                  <span key={device.deviceId}>
                    <span className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded m-1">
                      {device.deviceId} - {device.deviceName}
                      {!isEditing && <span onClick={() => handleDeviceSelection(device)} className="ml-2 cursor-pointer text-black">
                        X
                      </span>
                      }
                    </span>

                  </span>
                ))
              ) : (
                <p className="text-gray-500">No devices selected</p>
              )}
            </div>
            <div className="flex flex-row mt-4 justify-between">
              <div className="flex flex-col gap-3 w-1/5">
                <label className="font-semibold">Log Date & Time</label>
                <input
                  type="datetime-local"
                  name="logDateTime"
                  value={formData.logDateTime}
                  onChange={(e) => setFormData({ ...formData, logDateTime: e.target.value })}
                  className="border p-2 rounded"
                  required={!isEditing}
                  disabled={isEditing}
                />
              </div>
              <div className="flex flex-col gap-3 w-1/5">

                <label className="font-semibold">Status</label>
                <select name="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="border p-2 rounded">
                  <option value="Active">Active</option>
                  <option value="Dispatch">Dispatch</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div className="flex flex-col gap-3 w-1/5">
                <label className="font-semibold">Priority</label>
                <select name="priority" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="border p-2 rounded">
                  {[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div className="flex flex-col gap-3 w-1/5">
                <label className="font-semibold">Type</label>

                {isFollowUp ? (
                  <>
                    <p className="border p-2 rounded">
                      {mapType[formData.type]}
                    </p>
                  </>
                ) : (
                  <select name="type" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="border p-2 rounded" disabled={isFollowUp}>
                    <option value="T">Trouble Call</option>
                    <option value="F">Follow Up</option>
                    <option value="S">Service</option>
                  </select>)}
              </div>
            </div>

            <div className="flex flex-start mt-4">
              <button
                type="button"
                className={`px-4 py-2 rounded ${viewActions ? "bg-gray-200" : "bg-blue-500 text-white"}`}
                onClick={() => setViewActions(false)}
              >
                Notes
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded ${viewActions ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                onClick={() => setViewActions(true)}
              >
                Task
              </button>
            </div>

            {!viewActions ?
              (<div className="flex flex-col gap-3">

                <label className="font-semibold">Task Notes</label>
                <textarea name="taskNotes" value={formData.taskNotes} onChange={(e) => setFormData({ ...formData, taskNotes: e.target.value })} className="border p-2 rounded min-h-20" />

                <label className="font-semibold">Call Notes</label>
                <textarea name="callNotes" value={formData.callNotes} onChange={(e) => setFormData({ ...formData, callNotes: e.target.value })} className="border p-2 rounded min-h-20" />
              </div>)
              :
              (
                <>
                  {!isEditing && <button type="button" onClick={() => setIsActionModalOpen(true)} className="bg-gray-200 text-black px-4 py-2 rounded">
                    {isEditing ? "View Action Items" : "Manage Action Items"}
                  </button>}
                  <div className="flex flex-col gap-3">
                    <ActionItemsTable actionItems={actionItems} isEditing={isEditing} setActionItems={setActionItems} />
                  </div>
                </>
              )}
          </>
        </form>

        {/* Submit Button */}
        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded mt-4" onClick={handleSubmit}>
          {isEditing ? "Update Call Log" :
            isFollowUp ? "Create Follow Up" : "Create Call Log"}
        </button>
        <button onClick={onClose} className="bg-gray-200 text-black px-4 py-2 rounded mt-4 ml-2">
          Cancel
        </button>
      </div >

      <ActionItemsModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        actionItems={actionItems}
        setActionItems={setActionItems}
        callNumber={editingCallLog?.callNumber}
        isEditing={isEditing}
        mechanics={mechanics}
        defaultDate={formData.logDateTime}
      />

      {/* Device Selection Modal */}
      {isDeviceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-2/3 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Select Devices</h2>
            <div className="flex flex-col gap-2">
              {buildingDevices.map((device) => (
                <label key={device.deviceId} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.devices.some((d) => d.deviceId === device.deviceId)}
                    onChange={() => handleDeviceSelection(device)}
                    className="mr-2"
                  />
                  {device.deviceId} - {device.deviceName || "N/A"}  {device.deviceType}
                </label>
              ))}
            </div>
            <button onClick={() => setIsDeviceModalOpen(false)} className="bg-blue-500 text-white px-4 py-2 rounded mt-4">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogForm;